import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Image as ImageIcon, Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * ProjectPhotoUploadDialog — shows the project gallery + lets participants
 * append new photos. If the current user is not a member of the project,
 * the upload UI is hidden and a message is shown instead.
 */
export function ProjectPhotoUploadDialog({ open, onOpenChange, projectId, onUploaded }) {
  const fileRef = useRef(null);
  const [state, setState] = useState({ loading: true, name: "", gallery: [], can_upload: false });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    setState({ loading: true, name: "", gallery: [], can_upload: false });
    api.get(`/projects/${projectId}/gallery`)
      .then(({ data }) => setState({ loading: false, ...data }))
      .catch(() => setState({ loading: false, name: "", gallery: [], can_upload: false }));
  }, [open, projectId]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const file of files) {
        if (!file.type?.startsWith("image/")) continue;
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        paths.push(fileUrl(data.path));
      }
      if (paths.length === 0) {
        toast.error("Selecciona al menos una imagen");
        return;
      }
      const { data } = await api.post(`/projects/${projectId}/gallery`, { photos: paths });
      setState((s) => ({ ...s, gallery: data.gallery }));
      onUploaded?.({ project_id: projectId, gallery: data.gallery });
      toast.success(paths.length === 1 ? "Foto agregada al proyecto." : `${paths.length} fotos agregadas al proyecto.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No fue posible subir las fotos");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="project-photos-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Fotos del proyecto {state.name ? `— ${state.name}` : ""}
          </DialogTitle>
        </DialogHeader>

        {state.loading ? (
          <div className="text-sm text-[#5C6A7D] py-6 text-center">Cargando…</div>
        ) : (
          <>
            {state.gallery.length === 0 ? (
              <div className="border border-dashed border-[#E2E8F0] rounded-sm py-10 text-center text-sm text-[#5C6A7D]">
                <ImageIcon className="mx-auto mb-2 text-[#5C6A7D]" size={22} strokeWidth={1.5} />
                Este proyecto aún no tiene fotos.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[420px] overflow-auto" data-testid="project-gallery-grid">
                {state.gallery.map((p, i) => {
                  const when = p.uploaded_at ? new Date(p.uploaded_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;
                  const author = p.uploaded_by_name || p.uploaded_by_email;
                  return (
                    <div key={i} className="space-y-1" data-testid={`project-photo-${i}`}>
                      <div className="aspect-[4/3] rounded-sm overflow-hidden border border-[#E2E8F0]">
                        <img src={p.url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="px-0.5 leading-tight">
                        {author ? (
                          <div className="text-[10px] text-[#5C6A7D] truncate" title={p.uploaded_by_email || author} data-testid={`project-photo-author-${i}`}>{author}</div>
                        ) : <div className="text-[10px] text-[#94A3B8] italic">Autor no registrado</div>}
                        {p.uploaded_by_email && p.uploaded_by_email !== author && (
                          <div className="text-[9px] text-[#94A3B8] truncate">{p.uploaded_by_email}</div>
                        )}
                        {when && <div className="text-[9px] text-[#94A3B8] font-mono">{when}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {state.can_upload ? (
              <div className="pt-4 border-t border-[#E2E8F0]">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                  data-testid="project-photo-input"
                />
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full rounded-sm bg-[#10B981] hover:bg-[#059669] text-white h-11"
                  data-testid="project-photo-upload"
                >
                  <Upload size={16} className="mr-2" />
                  {uploading ? "Subiendo…" : "Subir fotos a este proyecto"}
                </Button>
              </div>
            ) : (
              <div className="pt-4 border-t border-[#E2E8F0]">
                <div className="text-xs text-[#5C6A7D] bg-[#FAFAFA] border border-[#E2E8F0] rounded-sm p-3 flex items-start gap-2">
                  <Lock size={14} className="mt-0.5 shrink-0" />
                  Solo los integrantes dados de alta en este proyecto pueden subir fotos.
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
